import {
    SectionHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { ILastProcessInEpisode, IServiceStartingPoint } from '@common/interfaces';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ServiceType } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { ViewMedicalDossierPatientConsentModal } from '@frontend/components/elements/viewMedicalDossierPatientConsentModal/ViewMedicalDossierPatientConsentModal';
import { useAbortableFetch } from '@frontend/utils';
import { checkForProcessesClosedByOtherRequesterOrganizations } from '@frontend/utils/checkForProcessesClosedByOtherRequesterOrganizations';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IProcess } from '../../interfaces/process';
import { translateKey } from '../../../translate';
import { Loader } from '../../elements/common';
import { FirstUseTour } from '../../elements/firstUseTour/FirstUseTour';
import { getStatusLabel } from '../../elements/getStatusLabel';
import { Modal } from '../../elements/modals/Modal';
import { NullableStringBirthDate } from '../../elements/NullableStringBirthDate';
import { ProcessStartWizard } from '../../elements/ProcessStartWizard/ProcessStartWizard';
import { ProcessStatusLabel } from '../../elements/processStatus/ProcessStatusLabel';
import { StatusLabelIcon } from '../../elements/StatusLabelIcon';
import { IconHeader } from '../../elements/textElements/iconHeader/IconHeader';
import { PageTemplate } from '../../templates/PageTemplate';
import { NewProcessButtonStartBar } from '../home/newProcess/NewProcessButtonStartBar';
import { RequestCreateActions } from '../requests/RequestCreateActions';
import { AllergiesList } from './allergies/AllergiesList';
import { EpisodeJournalList } from './episodeJournal/EpisodeJournalList';
import { LabResultsList } from './labResults/LabResultsList';
import { MedicationsList } from './medications/MedicationsList';
import { PatientAccessReasons } from './PatientAccessReasons';
import { PatientAvatarBlock } from './PatientAvatarBlock';
import { PatientInsuranceHistory } from './PatientInsuranceHistory';
import { PatientActiveInsuranceContextProvider } from './PatientInsuranceHistoryContext';
import { PatientSideBar } from './patientSideBar/PatientSideBar';
import { PatientMergeSso } from './ssoPatientFlow/PatientMergeSso';

export enum PatientDetailsOverview {
    allergies = 'allergies',
    episodeJournal = 'episodeJournal',
    insuranceHistory = 'insuranceHistory',
    labResults = 'labResults',
    medications = 'medications',
}

export function PatientDetails() {
    const navigate = useNavigate();
    const { id, ssoMergeId } = useParams() as { id: string; ssoMergeId?: string };
    const [searchParams] = useSearchParams();
    const overview = searchParams.getOptional('overview') as PatientDetailsOverview;
    const forwardToEpisode = searchParams.get('forwardToEpisode');
    const page = searchParams.getOptional('page');

    const abortableFetch = useAbortableFetch();
    const [patient, setPatient] = useState<IPatient>();
    const [selectedEpisodeCategory, setSelectedEpisodeCategory] = useState<EpisodeCategory>();
    const [requests, setRequests] = useState<IProcess[]>();
    const [requestsLoaded, setRequestsLoaded] = useState<boolean>();
    const [processes, setProcesses] = useState<ILastProcessInEpisode[]>();
    const [processesLoaded, setProcessesLoaded] = useState<boolean>();
    const [processButtonsLoaded, setProcessButtonsLoaded] = useState<boolean>();
    const [patientHasMedicalHistory, setPatientHasMedicalHistory] = useState(false);
    const [isViewMedicalDossierPatientConsentModalOpen, setIsViewMedicalDossierPatientConsentModalOpen] =
        useState(false);

    useEffect(() => {
        loadAll();
    }, [id, ssoMergeId]);

    useEffect(() => {
        return () => abortableFetch.abort();
    }, []);

    async function loadAll() {
        await Promise.all([loadPatient(), loadRequests(), loadProcesses(), checkHasPatientMedicalHistory()]);
    }

    if (!requests || !patient || !processes) {
        return (
            <Loader
                centered={true}
                large={true}
            />
        );
    }

    const hasProcessOrRequests = requests.length > 0 || processes.length > 0;
    const processesAndRequestsLoaded = requestsLoaded && processesLoaded;

    return (
        <>
            <Modal
                isOpen={ssoMergeId ? true : false}
                onClose={() => undefined}
                size="medium"
                title={translateKey('txcs.conflict')}
            >
                {ssoMergeId && (
                    <PatientMergeSso
                        onClose={async () => {
                            if (forwardToEpisode) {
                                navigate(`/episode?patientId=${id}&episodeCategory=${forwardToEpisode}`);
                            } else {
                                navigate(`/patients/${id}`);
                            }
                        }}
                        patientId={id}
                        ssoMergeId={ssoMergeId}
                    />
                )}
            </Modal>
            <PatientActiveInsuranceContextProvider patientId={patient.id}>
                <PageTemplate
                    sideBarNavigation={
                        <PatientSideBar
                            onClickShowViewMedicalDossierPatientConsentModal={
                                handleViewMedicalDossierPatientConsentModalCancel
                            }
                            onReload={async () => await loadAll()}
                            patient={patient}
                            patientHasMedicalHistory={patientHasMedicalHistory}
                        />
                    }
                    sideBarProfile={
                        <PatientAvatarBlock
                            patient={patient}
                            size="large"
                        />
                    }
                    topBarNavigation={
                        <PatientSideBar
                            onClickShowViewMedicalDossierPatientConsentModal={
                                handleViewMedicalDossierPatientConsentModalCancel
                            }
                            onReload={async () => await loadAll()}
                            patient={patient}
                        />
                    }
                    topBarProfile={
                        <PatientAvatarBlock
                            patient={patient}
                            size="small"
                        />
                    }
                >
                    {overview === undefined && (
                        <>
                            <RequestCreateActions
                                createRequest={async (serviceId, withProcedure) =>
                                    navigate(
                                        `/episode?patientId=$id}&serviceId=${serviceId}${
                                            withProcedure ? '&withProcedure=true' : ''
                                        }`
                                    )
                                }
                                patient={patient}
                            />
                            <ProcessStartWizard patient={patient}>
                                <NewProcessButtonStartBar
                                    label={translateKey('home.startOne')}
                                    onClear={onClear}
                                    onEpisodeCategorySelected={onEpisodeCategorySelected}
                                    onLoad={async () => await setProcessButtonsLoaded(true)}
                                    onServiceIdSelected={onServiceIdSelected}
                                    onStartingPointSelected={onStartingPointSelected}
                                    patient={patient}
                                />
                            </ProcessStartWizard>
                            <SectionHeader title={translateKey('episodeCategory.episodeCategories')} />
                            <>
                                {!processesAndRequestsLoaded ? (
                                    <Loader horizontalCentered={true} />
                                ) : (
                                    <PatientAccessReasons
                                        onCancel={() => navigate(-1)}
                                        onReasonGiven={() => loadAll()}
                                        patientId={id}
                                    >
                                        <>
                                            {hasProcessOrRequests ? (
                                                <TableContainer>
                                                    <Table spacing="medium">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableHeader>
                                                                    {translateKey('episodeCategory.episodeCategory')}
                                                                </TableHeader>
                                                                <TableHeader>
                                                                    {translateKey('request.status')}
                                                                </TableHeader>
                                                                <TableHeader>{'Laatste activiteit'}</TableHeader>
                                                                <TableHeader align="right">
                                                                    {'Laatste wijziging'}
                                                                </TableHeader>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {requests.map((item) => {
                                                                const label = getLabel(item);

                                                                return (
                                                                    <TableRow
                                                                        key={item.id}
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/episode?patientId=${patient.id}&episodeCategory=${item.episodeCategory}&requestId=${item.id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            {item.episodeCategory && (
                                                                                <IconHeader
                                                                                    icon={item.episodeCategory}
                                                                                    title={translateKey(
                                                                                        `episodeCategoryEnum.${item.episodeCategory}`
                                                                                    )}
                                                                                />
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <StatusLabelIcon statusLabel={label} />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {getServiceLabel(
                                                                                item.code,
                                                                                'patientDossier',
                                                                                item.name
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <NullableStringBirthDate
                                                                                value={item.sortDate}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                            {processes.map((item) => {
                                                                return (
                                                                    <TableRow
                                                                        key={item.id}
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/patients/${patient.id}/${item.service.episodeCategory.name}`
                                                                            )
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            <IconHeader
                                                                                icon={item.service.episodeCategory.name}
                                                                                title={translateKey(
                                                                                    `episodeCategoryEnum.${item.service.episodeCategory.name}`
                                                                                )}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <ProcessStatusLabel
                                                                                {...item.statusLabelData}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {getServiceLabel(
                                                                                item.service.code,
                                                                                'patientDossier',
                                                                                item.service.name
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <NullableStringBirthDate
                                                                                value={item.statusLabelData.date}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <EmptyListPlaceholder subtitle="Deze patiënt heeft nog geen (voor jou relevante) zorgpaden bij Ksyos" />
                                            )}
                                        </>
                                    </PatientAccessReasons>
                                )}
                            </>
                        </>
                    )}
                    {overview === 'medications' && (
                        <MedicationsList
                            page={page}
                            patientId={id}
                        />
                    )}
                    {overview === 'allergies' && (
                        <AllergiesList
                            page={page}
                            patientId={id}
                        />
                    )}
                    {overview === 'labResults' && (
                        <LabResultsList
                            page={page}
                            patientId={id}
                        />
                    )}
                    {overview === 'episodeJournal' && (
                        <EpisodeJournalList
                            page={page}
                            patientId={id}
                        />
                    )}
                    {overview === 'insuranceHistory' && <PatientInsuranceHistory patient={patient} />}
                    <ViewMedicalDossierPatientConsentModal
                        isOpen={isViewMedicalDossierPatientConsentModalOpen}
                        onCancel={handleViewMedicalDossierPatientConsentModalCancel}
                        onSaved={handleViewMedicalDossierPatientConsentModalSaved}
                        patientId={id}
                    />
                </PageTemplate>
            </PatientActiveInsuranceContextProvider>

            {processButtonsLoaded && requestsLoaded && <FirstUseTour url="/patients" />}
        </>
    );

    async function handleViewMedicalDossierPatientConsentModalSaved() {
        await loadAll();
        toggleViewMedicalDossierPatientConsentModal();
    }

    function handleViewMedicalDossierPatientConsentModalCancel() {
        toggleViewMedicalDossierPatientConsentModal();
    }

    function onServiceIdSelected(args: ServiceUrlObject) {
        const { hasStartingPoints, newProcessCustomIdentifier, serviceId, withProcedure } = args;
        if (serviceId && !hasStartingPoints) {
            const urlBuilder = new UrlBuilder('/episode');
            urlBuilder
                .add('episodeCategory', selectedEpisodeCategory!)
                .add('serviceId', serviceId)
                .add('withProcedure', withProcedure);

            if (newProcessCustomIdentifier) {
                urlBuilder.add('newProcessCustomIdentifier', newProcessCustomIdentifier);
            }

            urlBuilder.add('patientId', id);
            navigate(urlBuilder.build());
        }
    }

    async function onEpisodeCategorySelected(episodeCategory: EpisodeCategory | undefined) {
        setSelectedEpisodeCategory(episodeCategory);
    }

    async function onStartingPointSelected(startingPoint: IServiceStartingPoint) {
        const url = generateUrlForRoute(
            'patientCarePath',
            {
                activeTab: 'carePath',
                episodeCategory: startingPoint.service.episodeCategory.name,
                patientId: id,
            },
            {
                startingPointId: startingPoint.id,
            }
        );

        navigate(url);
    }

    function onClear() {
        setSelectedEpisodeCategory(undefined);
    }

    async function loadPatient() {
        const url = `/api/patients/${id}?includeItemAuthorizations=canUpdate,canSeeProfessionalSummaryDataDetails,canSeePatientDetails,canSeePatientAllergies,canSeePatientEpisodes,canSeePatientLabResults,canSeePatientMedication,canUpdatePatientConsent`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPatient(result.json);
    }

    async function loadRequests() {
        const url = `/api/requestProcesses?patientId=${id}&onlyLastInCarePaths=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setRequests(result.json.items);
        setRequestsLoaded(true);
    }

    async function loadProcesses() {
        const url = `/api/patients/${id}/lastProcessOfEpisode`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setProcesses(result.json.items);
        setProcessesLoaded(true);
    }

    async function checkHasPatientMedicalHistory() {
        const result = await checkForProcessesClosedByOtherRequesterOrganizations(id);
        setPatientHasMedicalHistory(result);
    }

    function getLabel(process: IProcess) {
        if (isCounseling(process)) {
            return getStatusLabel(process);
        }

        return process.statusLabel;
    }

    function isCounseling(process: IProcess) {
        return process.serviceType === ServiceType.counseling;
    }

    function toggleViewMedicalDossierPatientConsentModal() {
        setIsViewMedicalDossierPatientConsentModalOpen(!isViewMedicalDossierPatientConsentModalOpen);
    }
}
